import { RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Col,
  message,
  Modal,
  Row,
  Space,
  Tabs,
} from "antd";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  addProductApi,
  getProductByIdApi,
  updateProductApi,
} from "states/product/product.api";
import {
  ProductModel,
  ProductVariationAttributeModel,
} from "states/product/product.models";
import { ProductCategoryModel } from "states/product-category/product-category.models";
import { getProductCategoryListApi } from "states/product-category/product-category.api";
import { ProductAttributeTab } from "./components/product-attribute-tab";
import { t, CoreResource } from "shared/plugins/localization";
import { ProductInformationTab } from "./components/product-information-tab";
import { Seo } from "shared/components/seo/seo";
import { SeoModel } from "shared/models/seo-model";
import { CustomTheme } from "shared/components/custom-theme/custom-theme";
import { setLoading } from "states/global-value/global-value.slice";
import { ADD_PRODUCT_ROUTE, PRODUCT_ROUTE } from "shared/constants/routes";
import { fetchTagList } from "states/tag/tag.slice";
import {
  fetchProductAttributeGroupList,
  fetchProductAttributeList,
  fetchProductAttributeValueList,
} from "states/product-attribute/product-attribute.slice";
import { fetchUnitList } from "states/unit/unit.slice";
import useRefresh from "shared/hooks/use-refresh";
import { ProductUnitProducts } from "./components/product-unit-product";
import { RootState } from "app.store";
import { LanguageSelector } from "shared/components/language-selector";

interface RouteParams {
  productId: string;
}

export function AddUpdateProduct() {
  const refresh = useRefresh();
  const clientLanguages = useSelector(
    (state: RootState) => state.localization.clientLanguages,
    shallowEqual
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>();
  const { productId } = useParams<RouteParams>();
  const [model, setModel] = useState<ProductModel>({
    isActive: true,
    isInventory: false,
    variationAttributes: [] as ProductVariationAttributeModel[],
    unitProducts: [] as ProductModel[],
    unitRate: 1,
  } as ProductModel);
  const [mainVariationProduct, setMainVariationProduct] =
    useState<ProductModel>();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [productCategories, setProductCategories] = useState<
    ProductCategoryModel[]
  >([]);

  const dispatch = useDispatch();

  const getProduct = async () => {
    try {
      const res = await getProductByIdApi(Number(productId));
      setModel({
        ...res.data,
      });
      if (res.data.mainVariationProductId) {
        loadMainVariationProduct(res.data.mainVariationProductId);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, "DefaultErrorMessage")
      );
    }
  };

  const getProductCategories = async () => {
    try {
      const res = await getProductCategoryListApi();
      setProductCategories(res.data);
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, "DefaultErrorMessage")
      );
    }
  };

  const onSaved = async () => {
    dispatch(setLoading(true));
    try {
      const payload = {
        id: model.id,
        attributeGroups: model.attributeGroups,
        images: model.images,
        name: model.name,
        skuCode: model.skuCode,
        slug: model.slug,
        variationAttributes: model.variationAttributes,
        accessoryIds: model.accessoryIds,
        barCode: model.barCode,
        costPrice: model.costPrice,
        customizeTheme: model.customizeTheme,
        isAccessory: model.isAccessory,
        isActive: model.isActive,
        isInventory: model.isInventory,
        isVariation: model.isVariation,
        mainCategoryId: model.mainCategoryId,
        mainVariationProductId: model.mainVariationProductId,
        price: model.price,
        promotionFromDate: model.promotionFromDate,
        promotionPrice: model.promotionPrice,
        promotionToDate: model.promotionToDate,
        relatedCategoryIds: model.relatedCategoryIds,
        seoFields: model.seoFields,
        unitId: model.unit?.id,
        unitProducts: [],
        unitRate: model.unitRate,
        tagIds: model.tags ? model.tags.map((m) => m.id) : [],
        size: model.size,
        isModifier: model.isModifier,
        branchId: undefined,
        wholesale: model.wholesale,
        priceBySubTotal: model.priceBySubTotal,
        mainUnitProductId: undefined,
      };
      if (isUpdateMode) {
        await updateProductApi(payload, currentLanguage);
      } else {
        await addProductApi(payload, currentLanguage);
      }
      message.success(t(CoreResource, "UpdateDataSuccessfully"));
      refresh();
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, "DefaultErrorMessage")
      );
    }
    dispatch(setLoading(false));
  };

  const loadMainVariationProduct = (mainVariationProductId: number) => {
    getProductByIdApi(mainVariationProductId).then((res) => {
      setMainVariationProduct(res.data);
    });
  };

  useEffect(() => {
    if (document.location.href.indexOf(ADD_PRODUCT_ROUTE) > -1) {
      setIsUpdateMode(false);
      if (productId) {
        loadMainVariationProduct(Number(productId));
        setModel({
          ...model,
          isVariation: true,
          mainVariationProductId: Number(productId),
        });
      }
      document.title = "Thêm sản phẩm";
    } else {
      setIsUpdateMode(true);
      getProduct();
      document.title = "Cập nhật sản phẩm";
    }

    dispatch(fetchTagList());
    dispatch(fetchProductAttributeGroupList());
    dispatch(fetchProductAttributeList());
    dispatch(fetchProductAttributeValueList());
    dispatch(fetchUnitList());
    getProductCategories();
  }, []);

  useEffect(() => {
    if (mainVariationProduct && !isUpdateMode) {
      model.name = mainVariationProduct.name;
      model.mainCategoryId = mainVariationProduct.mainCategoryId;
      model.relatedCategoryIds = mainVariationProduct.relatedCategoryIds;
      model.unit = mainVariationProduct.unit;
      model.unitRate = mainVariationProduct.unitRate;
      model.isActive = mainVariationProduct.isActive;
      model.isInventory = mainVariationProduct.isInventory;
      model.images = mainVariationProduct.images;
      model.seoFields = mainVariationProduct.seoFields;
      model.variationAttributes = [];
      mainVariationProduct.variationAttributes.forEach((att) => {
        model.variationAttributes.push({
          variationAttributeId: att.variationAttributeId,
        } as ProductVariationAttributeModel);
      });
      setModel({ ...model });
    }
  }, [mainVariationProduct]);

  useEffect(() => {
    if (clientLanguages.length > 0 && !currentLanguage) {
      setCurrentLanguage(clientLanguages.find((m) => m.isDefault)?.langCode);
    }
  }, [clientLanguages]);

  return (
    <div className="add-update-product">
      <Row gutter={[16, 16]} className="mb-4">
        <Col flex="auto">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={PRODUCT_ROUTE}>Sản phẩm</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {isUpdateMode ? "Cập nhật" : "Thêm"}{" "}
              {mainVariationProduct
                ? `biến thể của ${mainVariationProduct.name}`
                : "sản phẩm"}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col flex="auto" className="d-flex justify-content-end">
          <Space>
            {isUpdateMode && (
              <LanguageSelector
                onChange={async (value) => {
                  const res = await getProductByIdApi(model.id, value);
                  if (!res.data.name) {
                    Modal.confirm({
                      title: "Thông báo",
                      content:
                        "Nội dung chưa được khởi tạo cho ngôn ngữ này, bạn có muốn khởi tạo?",
                      onOk: () => {
                        setCurrentLanguage(value);
                      },
                    });
                    return;
                  } else {
                    setCurrentLanguage(value);
                    setModel(res.data);
                  }
                }}
                selectedCode={currentLanguage}
              />
            )}
            <Link to={PRODUCT_ROUTE} className="mr-2">
              <Button type="default" icon={<RollbackOutlined />}>
                Trở lại
              </Button>
            </Link>
            <Button onClick={onSaved} type="primary" icon={<SaveOutlined />}>
              Lưu
            </Button>
          </Space>
        </Col>
      </Row>
      <Tabs type="card">
        <Tabs.TabPane tab="Thông tin" key="1">
          <ProductInformationTab
            isUnitProduct={false}
            model={model}
            onChange={setModel}
            productCategories={productCategories}
            isUpdateMode={isUpdateMode}
          />
        </Tabs.TabPane>
        {(!model.isVariation || !model.mainVariationProductId) && (
          <>
            <Tabs.TabPane tab={<span>Thuộc tính sản phẩm</span>} key="2">
              <ProductAttributeTab
                attributeGroups={model.attributeGroups ?? []}
                onUpdated={(attributeGroups) => {
                  setModel({ ...model, attributeGroups });
                }}
              ></ProductAttributeTab>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Giao diện" key="giao-dien">
              <CustomTheme
                onChange={(changedModel) => {
                  setModel({ ...model, customizeTheme: changedModel });
                }}
                model={model.customizeTheme ?? {}}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="SEO" key="seo">
              <Seo
                model={model.seoFields ?? ({} as SeoModel)}
                slug={model.slug}
                onChange={(seoFields, slug) => {
                  setModel({ ...model, slug, seoFields });
                }}
              />
            </Tabs.TabPane>
            {/* <Tabs.TabPane tab="Phụ kiện" key="3">
              Tính năng đang phát triển
            </Tabs.TabPane>
            <Tabs.TabPane tab="Sản phẩm mua kèm" key="4">
              Tính năng đang phát triển
            </Tabs.TabPane>
            <Tabs.TabPane tab="Bảo hành" key="6">
              Tính năng đang phát triển
            </Tabs.TabPane> */}
          </>
        )}
      </Tabs>
      {isUpdateMode && !model.isModifier && (
        <ProductUnitProducts
          mainProduct={model}
          productCategories={productCategories}
          isUpdateMode={isUpdateMode}
          refresh={refresh}
        />
      )}
    </div>
  );
}
